import { QueryClient } from '@tanstack/react-query';
import { Col, Divider, Row, Tooltip } from 'antd';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

import DownloadSvg from '../../../../assets/icons/DownloadSvg';
import EmojiThanks from '../../../../assets/icons/EmojiThanks';
import LinkSvg from '../../../../assets/icons/LinkSvg';
import { MagicWandSvg } from '../../../../assets/icons/MagicWandSvg';
import PlaySvg from '../../../../assets/icons/PlaySvg';
import ProfileChecked from '../../../../assets/icons/ProfileChecked';
import theme from '../../../../assets/theme';
import { Loader } from '../../../../components/_atoms/_Loader';
import { Center } from '../../../../components/_atoms/Center';
import ConditionalWrapper from '../../../../components/_atoms/ConditionalWrapper';
import { IconWrapper } from '../../../../components/_atoms/IconWrapper';
import Component404 from '../../../../components/_molecules/Component404';
import { NewLandingPageFooter } from '../../../../components/_molecules/LandingPageFooter';
import LessonCard from '../../../../components/_organisms/LessonCard';
import NewAppLayout from '../../../../components/layouts/NewAppLayout';
import { CenteredContainer } from '../../../../components/styled/CenteredContainer';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import useCreator from '../../../../hooks/useCreator';
import useCreatorTemplates, {
  CREATOR_TEMPLATES_QUERY_LIMIT,
} from '../../../../hooks/useCreatorTemplates';
import { useTemplateCreatorPhoto } from '../../../../hooks/useTemplateCreatorPhoto';
import backendService from '../../../../services/backendService';
import { IndexedTemplate } from '../../../../services/backendService/types';
import CreatorService from '../../../../services/CreatorService/CreatorService';
import {
  getDiscoverCreatorUrl,
  getDiscoverLessonUrl,
} from '../../../../services/DiscoverService/discoverUtils';
import { getBaseUrl } from '../../../../utils/client';
import { detectAndBestEffortSetLanguage } from '../../../../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../../../../utils/react-query-utils';

const CreatorPage = () => {
  const router = useRouter();
  const creatorId = router.query.creatorId as string | undefined;

  const { ref: scrollRef, inView } = useInView();
  const {
    creator,
    firstLetter,

    isFetchingCreator,
  } = useCreator(creatorId);
  const {
    canFetchMore,
    fetchMore,
    items: creatorTemplates,
    isLoading,
    isFetchingMore,
  } = useCreatorTemplates(creatorId);
  const isDesktop = useBreakPoint(800);
  const { t } = useTranslation('Host');
  const { thumbnail: creatorThumbnail } = useTemplateCreatorPhoto({
    creatorThumbnailId: creator?.thumbnailId,
    isPrivate: false,
  });

  useEffect(() => {
    if (inView && canFetchMore && !isFetchingMore) {
      fetchMore();
    }
  }, [inView, canFetchMore, fetchMore, isFetchingMore]);
  return (
    <NewAppLayout allowUnAuthenticated={true}>
      <Head>
        {creator && (
          <>
            <title>{`${creator.nickName} - Curipod`}</title>
            <meta
              property="og:title"
              content={`${creator.nickName} - Curipod`}
              key="title"
            />
            <meta name="twitter:title" content={`${creator.nickName} - Curipod`} />
            <meta
              name="description"
              content={creator.description || ''}
              key="namedescription"
            />
            <meta
              property="description"
              content={creator.description || ''}
              key="description"
            />
            <meta
              property="og:description"
              content={`${creator.description}` || ''}
              key="og:description"
            />
            <meta name="twitter:description" content={`${creator.description}` || ''} />
            <meta
              property="og:image"
              content={backendService.instance.discover.getCreatorThumbnailUrl(
                creator.thumbnailId || '',
              )}
              key="imageurl"
            />
            <meta
              property="og:image:secure_url"
              content={backendService.instance.discover.getCreatorThumbnailUrl(
                creator.thumbnailId || '',
              )}
              key="imagesecureurl"
            />
            <meta property="og:type" content="website" />

            <meta
              property="og:url"
              content={`${getBaseUrl()}${router.asPath}`}
              key="ogurl"
            />
            <meta property="og:image:type" content={'image/jpeg'} key="imagetype" />
            <meta
              property="og:image:alt"
              content={creator.description || ''}
              key="imageAlt"
            />
            <meta property="og:image:width" content={'300'} key="imageWidth" />
            <meta property="og:image:height" content={'190'} key="imageHeight" />
            <meta
              name="twitter:image"
              content={backendService.instance.discover.getCreatorThumbnailUrl(
                creator.thumbnailId || '',
              )}
            />
            <meta name="twitter:site" content={'@curipodofficial'} />
            <meta name="twitter:card" content={'summary'} />
            <meta name="twitter:widgets:theme" content="light" />
            <meta name="twitter:url" content={`${getBaseUrl()}${router.asPath}`} />
          </>
        )}
      </Head>

      <ConditionalWrapper
        condition={!isDesktop}
        truthyWrapper={(children: ReactNode) => (
          <CreatorLessonScrollContainer>{children}</CreatorLessonScrollContainer>
        )}
      >
        <Container style={{ flexDirection: 'column' }}>
          <CreatorLessonScrollContainer $isDesktop={isDesktop}>
            {isFetchingCreator && !creator && (
              <Center>
                <Loader />{' '}
              </Center>
            )}
            {!isFetchingCreator && !creator && (
              <Center>
                <Component404 />
              </Center>
            )}
            {creator && (
              <CreatorInfoContainer>
                <ProfileWrapper style={{ width: '100%' }}>
                  {creatorThumbnail ? (
                    <img
                      style={{
                        borderRadius: 250,
                        height: '100%',
                        width: '100%',
                        maxWidth: 200,
                      }}
                      src={creatorThumbnail}
                      alt="profile avatar"
                    />
                  ) : (
                    <Circle>
                      <span>{firstLetter}</span>
                    </Circle>
                  )}
                </ProfileWrapper>
                <div
                  style={{
                    margin: 0,
                    width: '100%',
                  }}
                >
                  <CreatorNameContainer>
                    <h1
                      style={{
                        position: 'relative',
                        margin: 0,
                        width: 'fit-content',
                        fontSize: '24px',
                        fontWeight: 'bold',
                      }}
                    >
                      {creator?.nickName}
                      <span
                        style={{
                          position: 'absolute',
                          top: 0,
                          right: -10,
                          transform: 'translateX(50%)',
                        }}
                      >
                        <Tooltip title={t('Verified')}>
                          <IconWrapper height="20px" width="20px" color={'#ffa55b'}>
                            <ProfileChecked />
                          </IconWrapper>
                        </Tooltip>
                      </span>
                    </h1>
                  </CreatorNameContainer>
                  {creator?.description && (
                    <>
                      <h3 style={{ color: theme.colors.iconGrey }}>About</h3>
                      <CreatorDescription
                        style={{
                          width: '100%',
                          fontWeight: 'bold',
                          textAlign: 'left',
                          padding: 0,
                          maxWidth: '800px',
                        }}
                      >
                        {creator?.description}
                      </CreatorDescription>
                    </>
                  )}
                  {creator?.bio && (
                    <div style={{ display: 'flex', padding: '10px' }}>
                      <IconWrapper style={{ marginRight: '10px' }}>
                        <LinkSvg />
                      </IconWrapper>

                      <div style={{ width: '100%', textAlign: 'left' }}>
                        <Link passHref href={creator?.bio}>
                          <a style={{ color: '#4f4f4f', fontWeight: 'bold' }}>
                            {creator.bio.length > 30
                              ? `${creator?.bio.slice(0, 30)}...`
                              : creator.bio}
                          </a>
                        </Link>
                      </div>
                    </div>
                  )}
                  <Divider
                    style={{ maxWidth: '300px', minWidth: '300px', width: '300px' }}
                  />
                  <CreatorStatsContainer>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '20px',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <IconWrapper
                          height="14px"
                          width="14px"
                          color="#828282"
                          style={{
                            gap: 5,
                            color: '#828282',
                            cursor: 'default',
                            fontWeight: 600,
                          }}
                        >
                          <MagicWandSvg /> Curipods
                        </IconWrapper>
                        <span
                          aria-label="Curipods"
                          style={{ fontWeight: 'bold', fontSize: 24 }}
                        >
                          {creator.curipodsCount || '0'}
                        </span>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <IconWrapper
                          height="16px"
                          width="16px"
                          color="#828282"
                          style={{
                            gap: 5,
                            color: '#828282',
                            cursor: 'default',
                            fontWeight: 600,
                          }}
                        >
                          <PlaySvg /> Plays
                        </IconWrapper>
                        <span
                          aria-label="Plays"
                          style={{ fontWeight: 'bold', fontSize: 24 }}
                        >
                          {creator.playsCount || '0'}
                        </span>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <IconWrapper
                          height="20px"
                          width="20px"
                          color="#828282"
                          style={{
                            gap: 5,
                            display: 'flex',
                            cursor: 'default',
                            color: '#828282',
                            fontWeight: 600,
                          }}
                        >
                          <DownloadSvg /> Copies
                        </IconWrapper>
                        <span
                          aria-label="Students"
                          style={{ fontWeight: 'bold', fontSize: 24 }}
                        >
                          {creator.downloadsCount || '0'}
                        </span>
                      </div>
                    </div>
                  </CreatorStatsContainer>
                  <Divider
                    style={{ maxWidth: '300px', minWidth: '300px', width: '300px' }}
                  />
                  {!isDesktop && creatorId && (
                    <>
                      <Divider />
                      <CreatorLessons
                        displayTemplates={creatorTemplates?.pages.flatMap(e => e.items)}
                        creatorId={creatorId}
                        isLoadingTemplates={isFetchingMore || isLoading}
                      />
                      <div ref={scrollRef}></div>
                    </>
                  )}
                </div>
              </CreatorInfoContainer>
            )}
            {isDesktop && creator && creatorId && (
              <>
                <CreatorLessons
                  creatorId={creatorId}
                  displayTemplates={creatorTemplates?.pages.flatMap(e => e.items)}
                  isLoadingTemplates={isFetchingMore || isLoading}
                />
                <div ref={scrollRef}></div>
                <NewLandingPageFooter footerBackgroundColor={theme.colors.paleBeige} />
              </>
            )}
          </CreatorLessonScrollContainer>
        </Container>
      </ConditionalWrapper>
    </NewAppLayout>
  );
};
const CreatorNameContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  margin-top: 20px;
`;
const CreatorLessonScrollContainer = styled.div<{ $isDesktop?: boolean }>`
  padding-top: 50px;
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;

const CreatorStatsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  margin-bottom: 14px;
`;

const CreatorDescription = styled.p`
  text-align: left;
  padding: 10px;
  padding-top: 20px;
  margin: 0;
  max-width: 300px;
  width: 100%;
  justify-content: center;
  inline-size: 150px;
  overflow-wrap: break-word;
  text-align: center;
`;

const CreatorInfoContainer = styled.div`
  width: 100%;
  padding: 70px 20px 20px 20px;
  display: grid;
  grid-template-columns: minmax(100px, 250px) auto;
  gap: 20px;
  align-items: center;

  @media (max-width: 800px) {
    display: flex;
    flex-direction: column;
  }
`;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  height: 100%;
  @media (min-width: 800px) {
    padding-left: 240px;
  }
`;

const Circle = styled.div<{ color?: string }>`
  border-radius: 100px;
  height: 100px;
  width: 100px;
  background-color: ${theme.colors.orange};
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  span {
    font-size: 40px;
    line-height: 40px;
  }
`;

const ProfileWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  position: relative;
  width: 100px;
`;

const CreatorLessons = ({
  displayTemplates,
  isLoadingTemplates,
}: {
  displayTemplates?: IndexedTemplate[];
  isLoadingTemplates: boolean;
  creatorId: string;
}) => {
  const { t } = useTranslation('Host');

  const isLargerThan600 = useBreakPoint(600);

  return (
    <>
      {!isLoadingTemplates && displayTemplates && displayTemplates.length === 0 && (
        <CenteredContainer
          style={{ height: '100%', width: '100%', flexDirection: 'column' }}
        >
          <EmojiThanks />
          <p style={{ fontWeight: 'bold', marginTop: 20 }}>
            {t('Your published curipods will appear here')}
          </p>
        </CenteredContainer>
      )}
      <Row
        style={{
          minHeight: 'calc(100% - 300px)', // make footer always be pushed to the bottom
          padding: isLargerThan600 ? '20px 20px 100px 20px' : '0px 0px 100px 0px',
          width: isLargerThan600 ? undefined : '100%',
          margin: 0,
        }}
        gutter={[20, 20]}
      >
        {displayTemplates &&
          displayTemplates?.map(template => {
            return (
              <Col
                xs={24}
                sm={12}
                md={24}
                lg={12}
                xl={8}
                xxl={6}
                style={{ height: 250 }}
                key={template.id}
              >
                <Card
                  template={template}
                  link={getDiscoverLessonUrl({
                    templateName: template.title,
                    templateId: template.id,
                  })}
                  authorLink={getDiscoverCreatorUrl(
                    template.creatorName,
                    template.creatorProfileId,
                  )}
                />
              </Col>
            );
          })}
        {isLoadingTemplates && (
          <Center>
            <Loader />
          </Center>
        )}
      </Row>
    </>
  );
};
const Card = styled(LessonCard)`
  width: 100%;
`;

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );
  const queryClient = new QueryClient();

  const { creatorId } = context.query as { creatorId: string };

  const query1 = queryClient.prefetchQuery(
    ['creators', creatorId],
    async () => CreatorService.getCreatorById(creatorId),
    { staleTime: 1000 * 60 * 5 },
  );

  const query2 = queryClient.prefetchInfiniteQuery(
    ['creatorTemplates', creatorId],
    async ({ pageParam = 0 }: { pageParam?: number }) => {
      const res = await CreatorService.getCreatorLessons(
        creatorId,
        `${CREATOR_TEMPLATES_QUERY_LIMIT}`,
        `${pageParam}`,
      );
      return res;
    },
    {
      getNextPageParam: lastPage => lastPage.cursor || null,
      staleTime: 1000 * 60 * 5,
    },
  );

  await Promise.all([query1, query2]);

  return {
    props: {
      dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
      ...(await detectAndBestEffortSetLanguage(context)),
    },
  };
};
export default CreatorPage;
